import { useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";

interface UserMenuProps {
  email: string;
}

export default function UserMenu({ email }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function handleSignOut() {
    if (!supabase) return;
    await supabase.auth.signOut();
    // Clear all user data from localStorage so the next sign-in starts clean.
    // Includes the AI chat history (Day 36) — privacy hardening so user A's
    // conversation isn't visible to user B after a user switch on the same
    // device.
    const keys = ["ppa:properties", "ppa:structures", "ppa:profile", "ppa:budget", "ppa:onboarded", "ppa:settings", "ppa:userId", "ppa:chat:messages"];
    keys.forEach((k) => localStorage.removeItem(k));
  }

  const truncated = email.length > 28 ? email.slice(0, 25) + "…" : email;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900 text-white text-[10px] font-semibold shrink-0">
          {email[0]?.toUpperCase() ?? "U"}
        </span>
        <span className="hidden sm:inline">{truncated}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-neutral-400">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-neutral-200 bg-white py-1 shadow-lg z-50">
          <div className="px-3 py-2 border-b border-neutral-100">
            <p className="text-xs font-medium text-neutral-900 truncate">{email}</p>
            <p className="text-xs text-neutral-400">Signed in</p>
          </div>
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
